import { defineStore } from 'pinia';
import { ISbStoryData } from '@storyblok/js';
import { getStories } from '@/composables/getStories';

// We previously duplicated the Storyblok fetch logic here, but that resulted in two separate caches.
// Instead we'll reuse the composable `getStories`, which already includes an in-memory cache and
// concurrency deduplication. This keeps a single source of truth and guarantees we never hit the
// Storyblok CDN more than once per build.

export const useStoriesStore = defineStore('stories', {
  actions: {
    async fetchData() {
      // only fetch data if this store is empty
      if (this.stories.length) return;

      // Fetch stories using the shared helper from the composable (includes its own cache)
      const stories = await getStories();

      this.stories = stories;
    },
  },
  getters: {
    getAllStories: (state: { stories: ISbStoryData[] }) => state.stories,
    getGDPR: (state: { stories: ISbStoryData[] }) => {
      return state.stories.find((item: ISbStoryData) => item.name === 'GDPR');
    },
    getGlobalFooter: (state: { stories: ISbStoryData[] }) => {
      return state.stories.find(
        (item: ISbStoryData) => item.name === 'Global Footer'
      );
    },
    getGlobalHeader: (state: { stories: ISbStoryData[] }) => {
      return state.stories.find(
        (item: ISbStoryData) => item.name === 'Global Header'
      );
    },
    getStoriesByType:
      (state: { stories: ISbStoryData[] }) => (types: string[]) => {
        return state.stories.reduce(
          (allItems: ISbStoryData[], item: ISbStoryData) => {
            if (
              item.content.component &&
              types.includes(item.content.component)
            ) {
              return [...allItems, item];
            } else {
              return allItems;
            }
          },
          []
        );
      },
    getStoriesInFolder:
      (state: { stories: ISbStoryData[] }) => (folder: string) => {
        return state.stories.reduce(
          (allItems: ISbStoryData[], item: ISbStoryData) => {
            const slug = item.full_slug;
            const createArray = (s: string) =>
              s.replace(/^\//, '').replace(/\/$/, '').split('/');
            const slugArray = createArray(slug);
            const folderArray = createArray(folder);

            if (
              slug.startsWith(`${folder}/`) &&
              slugArray.length > 0 &&
              folderArray.length > 0 &&
              folderArray.length <= slugArray.length &&
              folderArray.every((f, i) => slugArray[i] === f)
            ) {
              return [...allItems, item];
            } else {
              return allItems;
            }
          },
          []
        );
      },
    getStoryByFullSlug:
      (state: { stories: ISbStoryData[] }) => (fullSlug: string) => {
        if (state.stories.length)
          return state.stories.find(
            (item: ISbStoryData) =>
              item.full_slug.replace(/^\//, '').replace(/\/$/, '') ===
              fullSlug.replace(/^\//, '').replace(/\/$/, '')
          );
      },
    getStoryById: (state: { stories: ISbStoryData[] }) => (id: string) => {
      return state.stories.find((item) => item.uuid === id);
    },
  },
  state: () => ({
    stories: [] as ISbStoryData[],
  }),
});
